'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  TrashIcon,
  MessageSquareIcon,
  UserIcon,
  MailIcon,
  PhoneIcon,
  CalendarIcon,
  BookmarkCheckIcon,
  TextIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { ContentError, DeleteConfirmationDialog, UserAvatar } from '@/components/partials'
import { useContactFormMessage, useDeleteContactFormMessage, useCopyToClipboard } from '@/hooks'
import { useMeStore } from '@/store'
import { formatDateTime } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { DashboardResponseType } from '@/types/dashboard'

type PropsType = {
  id: string
}

const ContactFormMessageShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
        <Separator className="my-6" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-32 w-full rounded-md" />
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const locale = useLocale()
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const { data: message, isLoading, error } = useContactFormMessage(id)
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteContactFormMessage()
  const { meState } = useMeStore()
  const canDelete = hasPermission(meState.response, 'CONTACT_FORM_MESSAGES', 'DELETE')

  const decrementedRef = useRef(false)

  useEffect(() => {
    if (message?.readAt && !decrementedRef.current) {
      decrementedRef.current = true

      // Decrement unread count in dashboard cache directly — no API refetch
      queryClient.setQueryData<DashboardResponseType>(['dashboard'], old => {
        if (!old?.stats) {
          return old
        }
        return {
          ...old,
          stats: {
            ...old.stats,
            unreadContactFormMessages: Math.max(0, (old.stats.unreadContactFormMessages ?? 0) - 1)
          }
        }
      })

      // Update the item's readAt in the list cache so the row loses its unread styling
      queryClient.setQueriesData<{ items: any[] }>({ queryKey: ['contact-form-messages'] }, old => {
        if (!old?.items) {
          return old
        }
        return {
          ...old,
          items: old.items.map((item: any) => (item.id === id ? { ...item, readAt: message.readAt } : item))
        }
      })
    }
  }, [message?.readAt, queryClient, id])

  if (isLoading) {
    return <ContactFormMessageShowSkeleton />
  }
  if (error || !message) {
    return <ContentError error={error} listUrl="/user-interactions/contact-messages" />
  }

  const confirmDelete = () => {
    deleteMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['contact-form-messages'] })
        toast.success(t('deleted_successfully'))
        router.push('/user-interactions/contact-messages')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message ?? t('error_deleting'))
        setDeleteId(null)
      }
    })
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href="/user-interactions/contact-messages">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('back')}</span>
              </Link>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold">
                <MessageSquareIcon className="h-6 w-6" />
                {t('contact_form_message')}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground font-mono text-sm">{message.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(message.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          {canDelete && (
            <div className="flex flex-row items-center justify-end gap-2">
              <Button variant="destructive" size="sm" onClick={() => setDeleteId(id)}>
                <TrashIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('delete')}</span>
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('general_information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <UserIcon className="h-3.5 w-3.5" />
                  {t('sender')}
                </p>
                <p className="font-medium">{`${message.firstname} ${message.lastname}`}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MailIcon className="h-3.5 w-3.5" />
                  {t('email')}
                </p>
                <p className="text-sm">{message.email}</p>
              </div>

              {message.phone && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <PhoneIcon className="h-3.5 w-3.5" />
                    {t('phone')}
                  </p>
                  <p className="text-sm">{message.phone}</p>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <TextIcon className="h-3.5 w-3.5" />
                  {t('subject')}
                </p>
                <p className="font-medium">{message.subject}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <BookmarkCheckIcon className="h-3.5 w-3.5" />
                  {t('read_status')}
                </p>
                {message.readAt ? (
                  <Badge variant="success">{t('read')}</Badge>
                ) : (
                  <Badge variant="secondary">{t('unread')}</Badge>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {t('created_at')}
                </p>
                <p className="text-sm">{formatDateTime(message.createdAt, locale)}</p>
              </div>

              {message.readAt && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {t('read_at')}
                  </p>
                  <p className="text-sm">{formatDateTime(message.readAt, locale)}</p>
                </div>
              )}

              {message.firstReadUser && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <UserIcon className="h-3.5 w-3.5" />
                    {t('first_read_user')}
                  </p>
                  <Link href={`/users/${message.firstReadUser.id}`} className="flex items-center gap-2">
                    <UserAvatar user={message.firstReadUser} size="sm" />
                    <div className="flex flex-col">
                      <span className="text-sm">{message.firstReadUser.fullName}</span>
                      <span className="text-muted-foreground text-xs">{message.firstReadUser.email}</span>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {message.message && (
              <>
                <Separator className="my-6" />
                <div className="space-y-2">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
                    <MessageSquareIcon className="h-3.5 w-3.5" />
                    {t('message')}
                  </p>
                  <div className="bg-muted rounded-md p-4 text-sm">
                    {message.message!.split('\n').map((line, i, arr) => (
                      <span key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteId}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  )
}

export default Detail
